import React, { useState, useEffect, useCallback } from 'react'
import type { Block } from '../../editor/types'

interface PollBlockProps {
  block: Block
  demoId?: number
  onSetVariableValue?: (name: string, value: number | string | boolean) => void
}

interface PollOption {
  label: string
  value: string
}

function getConfig(block: Block) {
  const config = (block.config || {}) as Record<string, unknown>
  return {
    options: (config.options as PollOption[]) || [],
    showResults: (config.showResults as string) || 'after_vote',
    style: (config.style as string) || 'buttons',
  }
}

export default function PollBlock({ block, demoId, onSetVariableValue }: PollBlockProps) {
  const { options, showResults, style } = getConfig(block)
  const accent = block.accent || 'var(--accent, #5048e5)'

  const [results, setResults] = useState<Record<string, number>>({})
  const [total, setTotal] = useState(0)
  const [myVote, setMyVote] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [voted, setVoted] = useState(false)

  // Load saved vote from localStorage
  const storageKey = `explaina_poll_${demoId}_${block.id}`

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        setMyVote(saved)
        setVoted(true)
      }
    } catch { /* ignore */ }
  }, [storageKey])

  // Fetch results on mount
  useEffect(() => {
    if (!demoId) return
    fetch(`/api/v1/demos/${demoId}/blocks/${block.id}/results`)
      .then((res) => res.json())
      .then((data) => {
        setResults(data.results || {})
        setTotal(data.total || 0)
        if (data.my_vote) {
          setMyVote(data.my_vote)
          setVoted(true)
          try { localStorage.setItem(storageKey, data.my_vote) } catch { /* ignore */ }
        }
      })
      .catch(() => {})
  }, [demoId, block.id, storageKey])

  const vote = useCallback((value: string) => {
    if (loading) return

    // Optimistic update
    setMyVote(value)
    setVoted(true)
    setResults((prev) => {
      const next = { ...prev }
      next[value] = (next[value] || 0) + 1
      return next
    })
    setTotal((prev) => prev + 1)
    try { localStorage.setItem(storageKey, value) } catch { /* ignore */ }

    // Skip API call in preview mode
    if (!demoId) return

    setLoading(true)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    fetch(`/api/v1/demos/${demoId}/blocks/${block.id}/respond`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
      body: JSON.stringify({ value }),
    })
      .then((res) => res.json())
      .then((data) => {
        setResults(data.results || {})
        setTotal(data.total || 0)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [demoId, block.id, loading, storageKey])

  // Expose poll data as variables: @varName_total, @varName_optionValue
  useEffect(() => {
    const varName = block.variable
    if (!varName || !onSetVariableValue) return
    onSetVariableValue(`${varName}_total`, total)
    for (const opt of options) {
      onSetVariableValue(`${varName}_${opt.value}`, results[opt.value] || 0)
    }
  }, [results, total, block.variable, options, onSetVariableValue])

  const shouldShowResults =
    showResults === 'always' || (showResults === 'after_vote' && voted)

  // Preview mode (no demoId)
  const isPreview = !demoId

  return (
    <div className="space-y-3">
      {block.label && (
        <p className="text-base font-semibold text-slate-900">{block.label}</p>
      )}

      <div className={style === 'bar' ? 'space-y-2' : 'grid gap-2'} style={style === 'buttons' ? { gridTemplateColumns: `repeat(${Math.min(options.length, 3)}, 1fr)` } : undefined}>
        {options.map((opt) => {
          const count = results[opt.value] || 0
          const pct = total > 0 ? Math.round((count / total) * 100) : 0
          const isSelected = myVote === opt.value

          if (style === 'bar') {
            return (
              <button
                key={opt.value}
                onClick={() => !voted && vote(opt.value)}
                disabled={voted}
                className={`relative w-full text-left rounded-xl overflow-hidden transition-all ${
                  voted ? 'cursor-default' : 'cursor-pointer hover:shadow-md'
                } ${isSelected ? 'ring-2 ring-offset-1' : ''}`}
                style={{
                  ...(isSelected ? { ringColor: accent } as React.CSSProperties : {}),
                  border: `2px solid ${isSelected ? accent : '#e2e8f0'}`,
                }}
              >
                {/* Result bar background */}
                {shouldShowResults && (
                  <div
                    className="absolute inset-0 transition-all duration-700 ease-out"
                    style={{
                      width: `${pct}%`,
                      background: isSelected ? accent : '#f1f5f9',
                      opacity: isSelected ? 0.15 : 1,
                    }}
                  />
                )}
                <div className="relative flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2">
                    {isSelected && (
                      <span className="flex items-center justify-center w-5 h-5 rounded-full text-white text-xs" style={{ background: accent }}>
                        ✓
                      </span>
                    )}
                    <span className={`text-sm font-medium ${isSelected ? 'text-slate-900' : 'text-slate-700'}`}>
                      {opt.label}
                    </span>
                  </div>
                  {shouldShowResults && (
                    <span className="text-sm font-bold text-slate-500">{pct}%</span>
                  )}
                </div>
              </button>
            )
          }

          // Buttons style
          return (
            <button
              key={opt.value}
              onClick={() => !voted && vote(opt.value)}
              disabled={voted}
              className={`relative overflow-hidden rounded-xl transition-all ${
                voted ? 'cursor-default' : 'cursor-pointer hover:shadow-md hover:-translate-y-0.5'
              }`}
              style={{
                border: `2px solid ${isSelected ? accent : '#e2e8f0'}`,
                background: isSelected ? accent + '10' : 'white',
              }}
            >
              <div className="px-4 py-3 text-center">
                <span className={`text-sm font-semibold ${isSelected ? '' : 'text-slate-700'}`} style={isSelected ? { color: accent } : undefined}>
                  {opt.label}
                </span>
                {shouldShowResults && (
                  <div className="mt-1.5">
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${pct}%`, background: accent }}
                      />
                    </div>
                    <span className="text-xs font-bold text-slate-400 mt-1 block">{pct}%</span>
                  </div>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {shouldShowResults && total > 0 && (
        <p className="text-xs text-slate-400 text-center">
          {total} {total === 1 ? 'vote' : 'votes'}
        </p>
      )}

      {isPreview && voted && (
        <button
          onClick={() => { setMyVote(null); setVoted(false); setResults({}); setTotal(0) }}
          className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors mx-auto block"
        >
          Reset vote
        </button>
      )}
    </div>
  )
}
